import type { IntakeRun } from '@/lib/api/requirement-library-intake';
import { isRunInProgress } from '@/lib/api/requirement-library-intake';
import { plural } from '../library-logic';

/**
 * Pure rules of the app-wide intake run watcher (T-0157): which run changes
 * to announce, and what to say. The watcher component does the polling.
 */

export type RunTransition = { run: IntakeRun; kind: 'failed' | 'ready' };

/** The key a change is announced under: one announcement per run per outcome. */
export function transitionKey(run: Pick<IntakeRun, 'id' | 'status'>): string {
  return `${run.id}:${run.status}`;
}

/** When the run reached its current outcome. */
export function transitionTime(run: IntakeRun): number {
  const at =
    run.status === 'failed'
      ? (run.closed_at ?? run.updated_at)
      : run.status === 'ready_for_review'
        ? (run.extracted_at ?? run.updated_at)
        : run.updated_at;
  const ms = at ? Date.parse(at) : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

export interface TransitionContext {
  /** Runs this tab has seen still being read. */
  seenActive: ReadonlySet<string>;
  /** Changes already announced in this tab. */
  announced: ReadonlySet<string>;
  /**
   * The last time the watcher looked (server clock, ms), or null on a first
   * look without one. A run that ended after it is new to the curator.
   */
  sinceMs: number | null;
}

/**
 * The changes to announce now. A run that failed or became ready for review
 * is announced when this tab saw it running, or when it ended after the
 * last look (a run started in another tab, or that ended while the page was
 * closed). Never twice: the caller adds each returned key to `announced`.
 */
export function transitionsToAnnounce(
  runs: ReadonlyArray<IntakeRun>,
  ctx: TransitionContext,
): RunTransition[] {
  const out: RunTransition[] = [];
  const seen = new Set<string>();
  for (const run of runs) {
    if (run.status !== 'failed' && run.status !== 'ready_for_review') continue;
    const key = transitionKey(run);
    if (ctx.announced.has(key) || seen.has(key)) continue;
    const fresh =
      ctx.seenActive.has(run.id) || (ctx.sinceMs != null && transitionTime(run) >= ctx.sinceMs);
    if (!fresh) continue;
    seen.add(key);
    out.push({ run, kind: run.status === 'failed' ? 'failed' : 'ready' });
  }
  return out;
}

/** Ids of the runs still being read. */
export function activeRunIds(runs: ReadonlyArray<IntakeRun>): string[] {
  return runs.filter((r) => isRunInProgress(r.status)).map((r) => r.id);
}

/**
 * Why a run failed, in one plain line. Kept in step with the notification
 * taxonomy (`intakeFailureReason` in `@caliber-2/notification-fanout`).
 */
export function intakeFailureReason(code: string | null | undefined): string {
  switch (code) {
    case 'handoff_failed':
      return 'The document reader never picked it up (the hand-off never arrived).';
    case 'worker_stalled':
      return 'The document reader stopped part-way and did not recover.';
    case 'ai.budget_exhausted':
      return 'The firm AI budget for this month ran out. A firm admin can raise it under Firm settings, AI costs.';
    case 'parse_failed':
      return 'The file could not be opened (parse error).';
    case 'no_text':
      return 'No readable text was found in it.';
    case 'document_withdrawn':
      return 'It was withdrawn by a firm admin.';
    case 'extraction_failed':
      return 'The AI model could not read it (model error).';
    default:
      return code && code.startsWith('ai.')
        ? 'The AI model returned an error.'
        : 'Something went wrong while reading it.';
  }
}

/** True when "Start again" (read the stored document in a new run) is allowed. */
export function canStartAgain(run: IntakeRun): boolean {
  return run.error?.code !== 'document_withdrawn' && !run.document.withdrawn;
}

export function failedRunHeadline(run: IntakeRun): string {
  return `${run.document.file_name} could not be read.`;
}

export function readyRunMessage(run: IntakeRun): string {
  const n = run.counts.candidates ?? 0;
  if (run.outcome === 'none_found' || n === 0) {
    return `No requirements were found in ${run.document.file_name}.`;
  }
  return `${plural(n, 'candidate')} ready to review from ${run.document.file_name}.`;
}
